import { useState, useEffect, useCallback } from 'react';
import { FiTrash2, FiStar, FiSearch, FiExternalLink } from 'react-icons/fi';
import { apiAdminGetReviews, apiDeleteReview } from '../api';
import Pagination from '../components/Pagination';
import { storeUrl } from '../config/urls';
import { confirmAdminAction } from '../utils/adminConfirm';

const STAR_OPTIONS = [
  { value: '', label: 'All ratings' },
  { value: '5', label: '5 stars' },
  { value: '4', label: '4 stars' },
  { value: '3', label: '3 stars' },
  { value: '2', label: '2 stars' },
  { value: '1', label: '1 star' },
];

const Stars = ({ value }) => (
  <span className="inline-flex items-center text-amber-500">
    {Array.from({ length: 5 }, (_, i) => (
      <FiStar
        key={i}
        size={14}
        fill={i < value ? 'currentColor' : 'none'}
        className={i < value ? '' : 'text-black'}
      />
    ))}
  </span>
);

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleDateString(); } catch { return ''; }
};

// Reviews moderation list. Lets admins filter, search and delete spam/inappropriate reviews.
const ReviewsManager = () => {
  const [reviews, setReviews] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [rating, setRating] = useState('');
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiAdminGetReviews({
        page, limit: 20, rating, search: appliedSearch,
      });
      setReviews(data.reviews || []);
      setPagination(data.pagination || {});
    } catch { /* API client displays the error toast. */ } finally {
      setLoading(false);
    }
  }, [page, rating, appliedSearch]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (review) => {
    const confirmed = await confirmAdminAction(`Delete this review by ${review.user?.name || review.userName}?`, { title: 'Delete review', confirmLabel: 'Delete' });
    if (!confirmed) return;
    try {
      await apiDeleteReview(review.id);
      setReviews((prev) => prev.filter((r) => r.id !== review.id));
    } catch { /* API client displays the error toast. */ }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setAppliedSearch(search.trim());
  };

  return (
    <div className="space-y-4">
      <form onSubmit={handleSearch} className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-black" size={14} />
          <input
            type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search review text..."
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
        </div>
        <select
          value={rating} onChange={(e) => { setPage(1); setRating(e.target.value); }}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
        >
          {STAR_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <button
          type="submit"
          className="bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-orange-700"
        >
          Search
        </button>
      </form>

      <div className="text-xs text-black">
        {pagination.total == null ? '' : `${pagination.total} reviews`}
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full mx-auto"></div>
        </div>
      ) : (
        <div className="space-y-3">
          {reviews.map((rev) => (
            <article key={rev.id} className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Stars value={rev.rating} />
                    <span className="text-sm font-medium text-black">
                      {rev.user?.name || rev.userName}
                    </span>
                    <span className="text-xs text-black">{rev.user?.email}</span>
                    <span className="text-xs text-black">·</span>
                    <span className="text-xs text-black">{fmtDate(rev.createdAt)}</span>
                  </div>
                  {rev.book && (
                    <p className="text-xs text-black mt-1">
                      on <span className="font-medium text-black">{rev.book.title}</span>
                      {' '}by {rev.book.author}
                      <a
                        href={storeUrl(`/book/${rev.book.id}`)}
                        target="_blank" rel="noopener noreferrer"
                        className="ml-2 text-orange-600 hover:underline inline-flex items-center gap-1"
                      >
                        <FiExternalLink size={11} /> View
                      </a>
                    </p>
                  )}
                  {rev.text && (
                    <p className="text-sm text-black mt-2 whitespace-pre-wrap">{rev.text}</p>
                  )}
                  {rev.helpful > 0 && (
                    <p className="text-xs text-black mt-2">{rev.helpful} found helpful</p>
                  )}
                </div>
                <button
                  onClick={() => handleDelete(rev)}
                  className="text-red-500 hover:text-red-700"
                  title="Delete review"
                >
                  <FiTrash2 size={16} />
                </button>
              </div>
            </article>
          ))}

          {reviews.length === 0 && (
            <div className="text-center py-12 bg-white rounded-xl border border-gray-200 text-black">
              No reviews found
            </div>
          )}
        </div>
      )}

      <Pagination
        page={page}
        pages={pagination.pages || 1}
        onChange={setPage}
      />
    </div>
  );
};

export default ReviewsManager;
